"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KintsugiAdapter = exports.InterlayAdapter = exports.interlayTokensConfig = exports.kintsugiRoutersConfig = exports.interlayRoutersConfig = void 0;
const storage_1 = require("@acala-network/sdk/utils/storage");
const sdk_core_1 = require("@acala-network/sdk-core");
const rxjs_1 = require("rxjs");
const balance_adapter_1 = require("../balance-adapter");
const base_chain_adapter_1 = require("../base-chain-adapter");
const configs_1 = require("../configs");
const errors_1 = require("../errors");
const is_chain_equal_1 = require("../utils/is-chain-equal");
const DEST_WEIGHT = "180000000000";
exports.interlayRoutersConfig = [
    {
        to: "polkadot",
        token: "DOT",
        xcm: {
            fee: { token: "DOT", amount: "1000000000" },
            weightLimit: DEST_WEIGHT,
        },
    },
    {
        to: "statemint",
        token: "USDT",
        xcm: {
            // fees from tests with chopsticks: 700_000 atomic units
            fee: { token: "USDT", amount: "1000000" },
            weightLimit: DEST_WEIGHT,
        },
    },
];
exports.kintsugiRoutersConfig = [
    {
        to: "kusama",
        token: "KSM",
        xcm: {
            fee: { token: "KSM", amount: "1000000000" },
            weightLimit: DEST_WEIGHT,
        },
    },
    {
        to: "statemine",
        token: "USDT",
        xcm: {
            fee: { token: "USDT", amount: "10000" },
            weightLimit: DEST_WEIGHT,
        },
    },
];
exports.interlayTokensConfig = {
    interlay: {
        DOT: { name: "DOT", symbol: "DOT", decimals: 10, ed: "0" },
        USDT: { name: "USDT", symbol: "USDT", decimals: 6, ed: "0" },
    },
    kintsugi: {
        KSM: { name: "KSM", symbol: "KSM", decimals: 12, ed: "0" },
        USDT: { name: "USDT", symbol: "USDT", decimals: 6, ed: "0" },
    },
};
const KINTSUGI_SUPPORTED_TOKENS = {
    KSM: { Token: "KSM" },
    USDT: { ForeignAsset: 3 },
};
const INTERLAY_SUPPORTED_TOKENS = {
    DOT: { Token: "DOT" },
    USDT: { ForeignAsset: 2 },
};
const getSupportedTokens = (chainname) => {
    return chainname === "interlay"
        ? INTERLAY_SUPPORTED_TOKENS
        : KINTSUGI_SUPPORTED_TOKENS;
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createBalanceStorages = (api) => {
    return {
        assets: (address, token) => storage_1.Storage.create({
            api,
            path: "query.tokens.accounts",
            params: [address, token],
        }),
    };
};
class InterlayBalanceAdapter extends balance_adapter_1.BalanceAdapter {
    constructor({ api, chain, tokens }) {
        super({ api, chain, tokens });
        this.storages = createBalanceStorages(api);
    }
    subscribeBalance(token, address) {
        const tokenId = getSupportedTokens(this.chain)[token];
        if (tokenId === undefined) {
            throw new errors_1.CurrencyNotFound(token);
        }
        return this.storages.assets(address, tokenId).observable.pipe((0, rxjs_1.map)((balance) => {
            var _a;
            const amount = sdk_core_1.FixedPointNumber.fromInner(((_a = balance.free) === null || _a === void 0 ? void 0 : _a.toString()) || "0", this.getToken(token).decimals);
            return {
                free: amount,
                locked: new sdk_core_1.FixedPointNumber(0),
                reserved: new sdk_core_1.FixedPointNumber(0),
                available: amount,
            };
        }));
    }
}
class BaseInterlayAdapter extends base_chain_adapter_1.BaseCrossChainAdapter {
    async setApi(api) {
        this.api = api;
        await api.isReady;
        const chain = this.chain.id;
        this.balanceAdapter = new InterlayBalanceAdapter({
            chain,
            api,
            tokens: exports.interlayTokensConfig[chain],
        });
    }
    subscribeTokenBalance(token, address) {
        if (!this.balanceAdapter) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        return this.balanceAdapter.subscribeBalance(token, address);
    }
    subscribeMaxInput(token, address, to) {
        var _a;
        if (!this.balanceAdapter) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        return (0, rxjs_1.combineLatest)({
            txFee: token === ((_a = this.balanceAdapter) === null || _a === void 0 ? void 0 : _a.nativeToken)
                ? this.estimateTxFee({
                    amount: sdk_core_1.FixedPointNumber.ZERO,
                    to,
                    token,
                    address,
                    signer: address,
                })
                : "0",
            balance: this.balanceAdapter
                .subscribeBalance(token, address)
                .pipe((0, rxjs_1.map)((i) => i.available)),
        }).pipe((0, rxjs_1.map)(({ balance, txFee }) => {
            var _a;
            const tokenMeta = (_a = this.balanceAdapter) === null || _a === void 0 ? void 0 : _a.getToken(token);
            const feeFactor = 1.2;
            const fee = sdk_core_1.FixedPointNumber.fromInner(txFee, tokenMeta === null || tokenMeta === void 0 ? void 0 : tokenMeta.decimals).mul(new sdk_core_1.FixedPointNumber(feeFactor));
            // always minus ed
            return balance
                .minus(fee)
                .minus(sdk_core_1.FixedPointNumber.fromInner((tokenMeta === null || tokenMeta === void 0 ? void 0 : tokenMeta.ed) || "0", tokenMeta === null || tokenMeta === void 0 ? void 0 : tokenMeta.decimals));
        }));
    }
    createTx(params) {
        var _a;
        if (this.api === undefined) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        const { address, amount, to, token } = params;
        const toChain = configs_1.chains[to];
        const accountId = (_a = this.api) === null || _a === void 0 ? void 0 : _a.createType("AccountId32", address).toHex();
        const tokenId = getSupportedTokens(this.chain.id)[token];
        if (tokenId === undefined) {
            throw new errors_1.CurrencyNotFound(token);
        }
        // to other parachains
        let dst = {
            parents: 1,
            interior: {
                X2: [
                    { Parachain: toChain.paraChainId },
                    { AccountId32: { id: accountId, network: "Any" } },
                ],
            },
        };
        // to relay-chain
        if ((0, is_chain_equal_1.isChainEqual)(toChain, "kusama") || (0, is_chain_equal_1.isChainEqual)(toChain, "polkadot")) {
            dst = {
                interior: { X1: { AccountId32: { id: accountId, network: "Any" } } },
                parents: 1,
            };
        }
        // use "Unlimited" if the xToken.transfer's fourth parameter version supports it
        const destWeight = this.api.tx.xTokens.transfer.meta.args[3].type.toString() ===
            "XcmV2WeightLimit"
            ? "Unlimited"
            : this.getDestWeight(token, to);
        return this.api.tx.xTokens.transfer(tokenId, amount.toChainData(), {
            V1: dst,
        }, destWeight);
    }
}
class InterlayAdapter extends BaseInterlayAdapter {
    constructor() {
        super(configs_1.chains.interlay, exports.interlayRoutersConfig, exports.interlayTokensConfig.interlay);
    }
}
exports.InterlayAdapter = InterlayAdapter;
class KintsugiAdapter extends BaseInterlayAdapter {
    constructor() {
        super(configs_1.chains.kintsugi, exports.kintsugiRoutersConfig, exports.interlayTokensConfig.kintsugi);
    }
}
exports.KintsugiAdapter = KintsugiAdapter;
